// Canonical membership pricing for the public surface. The tier KEYS come from @forcebase-ai/core
// (`TIERS` / `Tier`) — the single source of truth (D9). This module supplies only the marketing
// presentation (price, copy, benefits) for each canonical tier; TypeScript's `Record<Tier, …>`
// fails the build if a tier is missing or renamed, so the page can never drift from core.
import { TIERS, type Tier } from "@forcebase-ai/core";

export type TierPlan = {
  tier: Tier;
  name: string;
  /** Monthly price in whole USD. */
  priceMonthly: number;
  /** Short italic accent line under the plan name. */
  tagline: string;
  /** One-line description below the tagline. */
  blurb: string;
  /** True when the plan is acquired by invitation, not self-checkout. */
  inviteOnly: boolean;
  benefits: string[];
  highlighted: boolean;
  /** Call-to-action button label. */
  cta: string;
};

const PLANS: Record<Tier, TierPlan> = {
  basic: {
    tier: "basic",
    name: "Basic",
    priceMonthly: 50,
    tagline: "Get in the ring",
    blurb: "Everything you need for on-demand care.",
    inviteOnly: false,
    benefits: [
      "Same-week telehealth visits",
      "Unlimited encrypted messaging",
      "Full access to Medical, Dental & Vision care",
      "Care at every camp & bout",
    ],
    highlighted: false,
    cta: "Get started",
  },
  premium: {
    tier: "premium",
    name: "Premium",
    priceMonthly: 100,
    tagline: "Your corner, on call",
    blurb: "Everything in Basic, plus a clinician who knows you.",
    inviteOnly: false,
    benefits: [
      "Everything in Basic",
      "Priority / next-day scheduling",
      "A dedicated clinician — the same doctor, every visit",
      "Priority messaging response",
    ],
    highlighted: true,
    cta: "Go Premium",
  },
  fighter: {
    tier: "fighter",
    name: "Fighter",
    priceMonthly: 0,
    tagline: "Cornered care for sponsored and pro athletes — by invitation.",
    blurb: "Everything in Premium, coordinated with your gym and corner.",
    inviteOnly: true,
    benefits: [
      "Everything in Premium",
      "Fight-week and fight-night clinician access",
      "Coordination with your gym and corner",
    ],
    highlighted: false,
    cta: "Request an invite",
  },
};

/** Plans in canonical core order (basic → premium → fighter). */
export const TIER_PLANS: TierPlan[] = TIERS.map((tier) => PLANS[tier]);

/** Self-purchasable plans only (fighter is invite-only and never self-checkout). */
export const SELF_SERVE_PLANS: TierPlan[] = TIER_PLANS.filter((p) => !p.inviteOnly);
